import React, { useMemo, useState } from 'react';
import axios from 'axios';
import VisualCheckpointEditor from './VisualCheckpointEditor';
import {
  createWaitToken,
  formatSequenceStep,
  isWaitToken,
  normalizeWaitMs,
  profileStepFromToken,
  sequenceDelayMs,
  tokensFromProfileSteps,
  waitMsFromToken,
} from './automationSequenceSteps';

const ACTIONS = ['open_bluetooth_settings', 'home', 'back', 'up', 'down', 'left', 'right', 'enter'];
const OPERATIONS = [
  { value: 'connect', label: 'Connect paired receiver' },
  { value: 'disconnect', label: 'Disconnect receiver' },
  { value: 'pair_connect', label: 'Pair and connect' },
  { value: 'unpair', label: 'Disconnect and unpair' },
];

const cleanId = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-|-$/g, '').slice(0, 48);

function finalObservations(operation, receiver) {
  const identity = {
    ...(receiver.address ? { deviceAddress: receiver.address } : {}),
    ...(receiver.name ? { deviceName: receiver.name } : {}),
  };
  if (operation === 'pair_connect') {
    return [
      { type: 'bluetooth_bond', expectedState: 'bonded', ...identity },
      { type: 'bluetooth_a2dp', expectedState: 'connected', ...identity },
    ];
  }
  if (operation === 'unpair') return [{ type: 'bluetooth_bond', expectedState: 'unbonded', ...identity }];
  return [{
    type: 'bluetooth_a2dp',
    expectedState: operation === 'disconnect' ? 'disconnected' : 'connected',
    ...identity,
  }];
}

function buildProfile(device, operation, receiver, actions, stepCheckpoints, stepDelayMs, profileId) {
  const operationLabel = OPERATIONS.find((entry) => entry.value === operation)?.label || operation;
  const appNode = device?.integrationData?.appNode || device?.integration_data?.appNode || {};
  return {
    id: profileId || cleanId(`bluetooth-${device.id}-${operation}-${receiver.address || receiver.name}`),
    name: `${operationLabel} · ${receiver.name || receiver.address}`,
    goal: 'bluetooth_output',
    targetDeviceId: Number(device.id),
    operation,
    trainingState: 'draft',
    deviceFingerprint: {
      manufacturer: appNode.manufacturer || null,
      model: appNode.model || device.model || null,
      androidRelease: appNode.androidRelease || null,
    },
    steps: actions.map((action, index) => (
      profileStepFromToken(action, stepDelayMs, stepCheckpoints[index])
    )),
    finalVerification: {
      id: `bluetooth-${operation}-verified`,
      match: 'all',
      observations: finalObservations(operation, receiver),
      maxAttempts: 4,
      retryDelayMs: 1250,
      recoverySteps: [],
    },
    limits: {
      maxDurationMs: Math.max(30000, Math.min(120000, sequenceDelayMs(actions, stepDelayMs) + 20000)),
      maxActions: 40,
    },
  };
}

export default function BluetoothAutomationTrainer({
  areaId,
  device,
  snapshot,
  topology,
  onTopologyChange,
  onOpenSettings,
}) {
  const [operation, setOperation] = useState('connect');
  const [receiverName, setReceiverName] = useState('');
  const [receiverAddress, setReceiverAddress] = useState('');
  const [actions, setActions] = useState([]);
  const [stepCheckpoints, setStepCheckpoints] = useState([]);
  const [stepDelayMs, setStepDelayMs] = useState(800);
  const [profileId, setProfileId] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [busy, setBusy] = useState('');
  const [liveAction, setLiveAction] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [checkpointEditor, setCheckpointEditor] = useState(null);
  const [stepIndex, setStepIndex] = useState(null);

  const ownerScreen = useMemo(() => (topology?.screens || []).find((screen) => (
    Number(screen.televisionDeviceId) === Number(device?.id)
    || (screen.players || []).some((player) => Number(player.deviceId || player.id) === Number(device?.id))
  )) || null, [device?.id, topology?.screens]);
  const savedProfiles = useMemo(() => (ownerScreen?.activationStrategy?.automationProfiles || [])
    .filter((entry) => entry.goal === 'bluetooth_output' && Number(entry.targetDeviceId) === Number(device?.id)), [device?.id, ownerScreen]);
  const knownReceivers = useMemo(() => {
    const bonded = snapshot?.bluetooth?.bondedDevices || [];
    const connected = snapshot?.bluetooth?.a2dp?.connectedDevices || [];
    const byIdentity = new Map();
    [...connected, ...bonded].forEach((entry) => {
      const key = String(entry.address || entry.name || '').toLowerCase();
      if (key) byIdentity.set(key, { ...byIdentity.get(key), ...entry });
    });
    return [...byIdentity.values()];
  }, [snapshot]);
  const receiver = { name: receiverName.trim(), address: receiverAddress.trim() };
  const profile = device && (receiver.name || receiver.address)
    ? buildProfile(device, operation, receiver, actions, stepCheckpoints, stepDelayMs, profileId)
    : null;
  const identityMatches = (entry) => (
    receiver.address && String(entry.address || '').toLowerCase() === receiver.address.toLowerCase()
  ) || (
    receiver.name && String(entry.name || '').toLowerCase() === receiver.name.toLowerCase()
  );
  const connectedDevices = snapshot?.bluetooth?.a2dp?.connectedDevices || [];
  const bondedDevices = snapshot?.bluetooth?.bondedDevices || [];
  const a2dpAvailable = snapshot?.bluetooth?.a2dp?.available === true;
  const bondAvailable = snapshot?.bluetooth?.supported === true && snapshot.bluetooth.permissionGranted !== false;
  const isConnected = connectedDevices.some(identityMatches);
  const isBonded = bondedDevices.some(identityMatches);
  const liveBluetoothConfirmed = operation === 'connect'
    ? a2dpAvailable && isConnected
    : operation === 'disconnect'
      ? a2dpAvailable && !isConnected
      : operation === 'pair_connect'
        ? a2dpAvailable && bondAvailable && isConnected && isBonded
        : bondAvailable && !isBonded;
  const executionCompleted = Boolean(result && result.status !== 'planned' && Number(result.actionsSent) > 0);
  const telemetryConfirmed = result?.status === 'verified' || (executionCompleted && liveBluetoothConfirmed);

  const selectReceiver = (value) => {
    const entry = knownReceivers.find((candidate) => (candidate.address || candidate.name) === value);
    setReceiverName(entry?.name || '');
    setReceiverAddress(entry?.address || '');
    setProfileId('');
    setSelectedProfileId('');
    setStepIndex(null);
    setResult(null);
  };

  const cloneRoutine = () => {
    if (!actions.length || (!receiver.name && !receiver.address)) {
      setMessage('Choose a receiver and record or load a routine before cloning it.');
      return;
    }
    const receiverMatches = (saved) => {
      const observation = saved.finalVerification?.observations?.[0] || {};
      return receiver.address
        ? String(observation.deviceAddress || '').toLowerCase() === receiver.address.toLowerCase()
        : String(observation.deviceName || '').toLowerCase() === receiver.name.toLowerCase();
    };
    const configured = new Set(savedProfiles.filter(receiverMatches).map((entry) => entry.operation));
    const nextOperation = OPERATIONS.find((entry) => entry.value !== operation && !configured.has(entry.value))?.value;
    setProfileId('');
    setSelectedProfileId('');
    setStepIndex(null);
    if (nextOperation) setOperation(nextOperation);
    setResult(null);
    setMessage(nextOperation
      ? `Keystrokes cloned for ${OPERATIONS.find((entry) => entry.value === nextOperation)?.label.toLowerCase()}. Adjust the final navigation, test, then save.`
      : 'Keystrokes cloned. All four operations already have saved routines; select which operation this copy should replace.');
  };

  const loadProfile = (saved) => {
    if (!saved) return;
    const observation = saved.finalVerification?.observations?.[0] || {};
    setSelectedProfileId(saved.id);
    setProfileId(saved.id);
    setOperation(saved.operation || 'connect');
    setReceiverName(observation.deviceName || '');
    setReceiverAddress(observation.deviceAddress || '');
    const sequence = tokensFromProfileSteps(saved.steps);
    setActions(sequence.tokens);
    setStepCheckpoints(sequence.checkpoints);
    const firstCommand = (saved.steps || []).find((step) => step.action !== 'wait');
    setStepDelayMs(Number(firstCommand?.delayAfterMs) || 800);
    setMessage(`${saved.name} loaded.`);
    setResult(null);
    setStepIndex(null);
  };

  const sendLiveAction = async (action) => {
    if (action === 'open_bluetooth_settings') {
      onOpenSettings?.();
      return;
    }
    await axios.post(
      `/api/site-control/targets/${device.id}/action`,
      { action, repeat: 1, intervalMs: 35 },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
  };

  const recordAction = async (action, insertAt = null) => {
    const resolvedInsertAt = Number.isInteger(insertAt)
      ? insertAt
      : Number.isInteger(stepIndex)
        ? Math.min(stepIndex + 1, actions.length)
        : null;
    setResult(null);
    setActions((current) => {
      if (!Number.isInteger(resolvedInsertAt)) return [...current, action];
      const next = [...current];
      next.splice(resolvedInsertAt, 0, action);
      return next;
    });
    setStepCheckpoints((current) => {
      if (!Number.isInteger(resolvedInsertAt)) return [...current, null];
      const next = [...current];
      next.splice(resolvedInsertAt, 0, null);
      return next;
    });
    if (Number.isInteger(stepIndex)) setStepIndex(resolvedInsertAt);
    setLiveAction(action);
    try {
      await sendLiveAction(action);
      const recordedIndex = Number.isInteger(resolvedInsertAt) ? resolvedInsertAt : actions.length;
      setMessage(`${action.replaceAll('_', ' ')} sent and recorded as step ${recordedIndex + 1}.`);
    } catch (error) {
      setMessage(`Step recorded, but live control failed: ${error?.response?.data?.message || error.message}.`);
    } finally {
      setLiveAction('');
    }
  };

  const recordWait = (durationMs = 2000, insertAt = null) => {
    const waitToken = createWaitToken(durationMs);
    const resolvedInsertAt = Number.isInteger(insertAt)
      ? insertAt
      : Number.isInteger(stepIndex)
        ? Math.min(stepIndex + 1, actions.length)
        : null;
    setResult(null);
    setActions((current) => {
      if (!Number.isInteger(resolvedInsertAt)) return [...current, waitToken];
      const next = [...current];
      next.splice(resolvedInsertAt, 0, waitToken);
      return next;
    });
    setStepCheckpoints((current) => {
      if (!Number.isInteger(resolvedInsertAt)) return [...current, null];
      const next = [...current];
      next.splice(resolvedInsertAt, 0, null);
      return next;
    });
    if (Number.isInteger(stepIndex)) setStepIndex(resolvedInsertAt);
    setMessage('Wait 2s added. Tap the wait step to set a different duration.');
  };

  const editWait = (index) => {
    const currentMs = waitMsFromToken(actions[index]);
    if (currentMs == null) return;
    const value = window.prompt('Wait duration in seconds (0.1–30)', String(currentMs / 1000));
    if (value == null) return;
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) {
      setMessage('Enter a wait duration between 0.1 and 30 seconds.');
      return;
    }
    const durationMs = normalizeWaitMs(seconds * 1000);
    setActions((current) => current.map((entry, itemIndex) => (
      itemIndex === index ? createWaitToken(durationMs) : entry
    )));
    setResult(null);
    setMessage(`${formatSequenceStep(createWaitToken(durationMs))} saved in the Bluetooth routine.`);
  };

  const removeStep = (index) => {
    setActions((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setStepCheckpoints((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setStepIndex(null);
    setResult(null);
  };

  const captureCheckpoint = async (targetStepIndex) => {
    if (!device?.id) return;
    setBusy('checkpoint');
    setMessage('Capturing the current Bluetooth settings screen…');
    try {
      const response = await axios.post(
        `/api/site-control/targets/${device.id}/checkpoint-state`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCheckpointEditor({ stepIndex: targetStepIndex, screenshotDataUrl: response.data.screenshotDataUrl });
      setMessage('Draw around the part of the screen that confirms this Bluetooth state.');
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Could not capture a checkpoint screenshot.');
    } finally {
      setBusy('');
    }
  };

  const addCheckpointAt = (destinationIndex) => {
    if (!actions.length || destinationIndex < 1) {
      setMessage('Add a command before placing a checkpoint.');
      return;
    }
    const targetStepIndex = Math.min(destinationIndex - 1, actions.length - 1);
    if (stepCheckpoints[targetStepIndex]) {
      setMessage('There is already a checkpoint at this point in the flow.');
      return;
    }
    captureCheckpoint(targetStepIndex);
  };

  const saveCheckpoint = (checkpoint) => {
    setStepCheckpoints((current) => current.map((entry, index) => index === checkpointEditor.stepIndex ? checkpoint : entry));
    setCheckpointEditor(null);
    setResult(null);
    setMessage('Visual checkpoint added to the Bluetooth routine.');
  };

  const setDragData = (event, payload) => {
    event.dataTransfer.effectAllowed = payload.source === 'palette' ? 'copy' : 'move';
    event.dataTransfer.setData('application/x-soundscreen-command', JSON.stringify(payload));
  };

  const dropStep = (event, destinationIndex) => {
    event.preventDefault();
    let dragged;
    try { dragged = JSON.parse(event.dataTransfer.getData('application/x-soundscreen-command')); } catch { return; }
    if (dragged.source === 'palette' && dragged.kind === 'checkpoint') return addCheckpointAt(destinationIndex);
    if (dragged.source === 'palette' && dragged.kind === 'wait') return recordWait(dragged.durationMs, destinationIndex);
    if (dragged.source === 'palette' && ACTIONS.includes(dragged.action)) return recordAction(dragged.action, destinationIndex);
    if (dragged.source !== 'sequence' || !Number.isInteger(dragged.index)) return;
    setActions((current) => {
      const next = [...current];
      const [action] = next.splice(dragged.index, 1);
      const adjusted = dragged.index < destinationIndex ? destinationIndex - 1 : destinationIndex;
      next.splice(adjusted, 0, action);
      return next;
    });
    setStepCheckpoints((current) => {
      const next = [...current];
      const [checkpoint] = next.splice(dragged.index, 1);
      const adjusted = dragged.index < destinationIndex ? destinationIndex - 1 : destinationIndex;
      next.splice(adjusted, 0, checkpoint || null);
      return next;
    });
    setStepIndex(null);
    setResult(null);
    setMessage('Bluetooth command order updated.');
  };

  const executeNextStep = async () => {
    if (!actions.length) return;
    const nextIndex = !Number.isInteger(stepIndex) || stepIndex >= actions.length - 1 ? 0 : stepIndex + 1;
    const action = actions[nextIndex];
    const waitMs = waitMsFromToken(action);
    const label = formatSequenceStep(action);
    setBusy('step');
    setLiveAction(waitMs == null ? action : '');
    setStepIndex(nextIndex);
    try {
      if (waitMs != null) {
        setMessage(`Step ${nextIndex + 1}: ${label.toLowerCase()}…`);
        await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      } else {
        await sendLiveAction(action);
      }
      setMessage(`Step ${nextIndex + 1} completed: ${label}. Continue stepping or insert the next action here.`);
    } catch (error) {
      setMessage(`Step ${nextIndex + 1} failed: ${error?.response?.data?.message || error.message || 'command failed'}.`);
    } finally {
      setLiveAction('');
      setBusy('');
    }
  };

  const callRunner = async (dryRun) => {
    if (!profile || !actions.length) {
      setMessage('Choose or enter a receiver and record at least one action first.');
      return;
    }
    if (!dryRun && operation === 'unpair' && !window.confirm(`Run the unpair routine for ${receiver.name || receiver.address}? It will need to be paired again before it can reconnect.`)) {
      return;
    }
    setBusy(dryRun ? 'plan' : 'execute');
    setResult(null);
    try {
      const response = await axios.post(
        `/api/site-control/targets/${device.id}/automation`,
        { profile, dryRun },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setResult(response.data);
      setMessage(response.data.status === 'verified'
        ? `${profile.name} verified by Bluetooth telemetry.`
        : response.data.status === 'planned'
          ? `${response.data.plan?.actionCount || 0} action(s); ${response.data.plan?.executable ? 'ready to test' : 'not executable'}.`
          : `${profile.name}: ${response.data.status}.`);
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Bluetooth automation failed.');
    } finally {
      setBusy('');
    }
  };

  const save = async (candidate = profile) => {
    if (!candidate || !actions.length) {
      setMessage('Choose or enter a receiver and record at least one action before saving.');
      return;
    }
    if (!ownerScreen) {
      setMessage('This device must be assigned to a Logical Screen before its Bluetooth routines can be saved.');
      return;
    }
    const existing = ownerScreen.activationStrategy?.automationProfiles || [];
    const nextProfiles = [...existing.filter((entry) => entry.id !== candidate.id), candidate];
    const nextTopology = {
      ...topology,
      screens: topology.screens.map((screen) => screen.id !== ownerScreen.id ? screen : ({
        ...screen,
        activationStrategy: { ...screen.activationStrategy, automationProfiles: nextProfiles },
      })),
    };
    setBusy('save');
    try {
      const response = await axios.put(`/api/areas/${areaId}/topology`, nextTopology, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const persistedScreen = (response.data?.screens || []).find((screen) => (
        Number(screen.televisionDeviceId) === Number(ownerScreen.televisionDeviceId)
      ));
      const persistedProfile = (persistedScreen?.activationStrategy?.automationProfiles || [])
        .find((entry) => entry.id === candidate.id);
      if (!persistedProfile) {
        throw new Error('The server saved the Screen topology but did not return this routine.');
      }
      if (candidate.trainingState === 'validated' && persistedProfile.trainingState !== 'validated') {
        throw new Error('The server did not retain the enabled state for this routine.');
      }
      onTopologyChange?.(response.data);
      loadProfile(persistedProfile);
      setMessage(candidate.trainingState === 'validated'
        ? `${candidate.name} enabled as a verified routine.`
        : `${candidate.name} saved as a draft. Test it successfully before enabling it for one-press control.`);
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Could not save the Bluetooth routine.');
    } finally {
      setBusy('');
    }
  };

  const enableRoutine = (installerConfirmed = false) => {
    if ((!telemetryConfirmed && !installerConfirmed) || !profile) return;
    if (installerConfirmed && !window.confirm('Enable this routine without Bluetooth telemetry confirmation? Use this only after observing the requested connection change.')) return;
    const verificationMethod = operation === 'pair_connect'
      ? 'bluetooth_bond+a2dp'
      : operation === 'unpair'
        ? 'bluetooth_bond'
        : 'bluetooth_a2dp';
    save({
      ...profile,
      trainingState: 'validated',
      validation: {
        validatedAt: new Date().toISOString(),
        verificationMethod: installerConfirmed ? 'installer_observation' : verificationMethod,
        result: installerConfirmed ? 'installer_confirmed' : 'verified',
        evidenceSource: installerConfirmed ? 'installer' : 'android.bluetooth',
      },
    });
  };

  if (!device) return null;
  return (
    <div className="bluetooth-automation-trainer">
      <header><div><span>Checkpoint automation</span><h4>Bluetooth routine trainer</h4></div><strong>Telemetry verified</strong></header>
      <div className="bluetooth-automation-trainer__fields">
        <label>Operation<select value={operation} onChange={(event) => { setOperation(event.target.value); setProfileId(''); setSelectedProfileId(''); setStepIndex(null); setResult(null); }}>{OPERATIONS.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}</select></label>
        <label>Known receiver<select value={receiverAddress || receiverName} onChange={(event) => selectReceiver(event.target.value)}><option value="">Enter manually…</option>{knownReceivers.map((entry) => <option key={entry.address || entry.name} value={entry.address || entry.name}>{entry.name || entry.address}{entry.address ? ` · ${entry.address}` : ''}</option>)}</select></label>
        <label>Receiver name<input value={receiverName} onChange={(event) => { setReceiverName(event.target.value); setProfileId(''); setSelectedProfileId(''); setResult(null); }} placeholder="House Audio" /></label>
        <label>Receiver address<input value={receiverAddress} onChange={(event) => { setReceiverAddress(event.target.value); setProfileId(''); setSelectedProfileId(''); setResult(null); }} placeholder="Optional MAC address" /></label>
        <label>Playback delay<select value={stepDelayMs} onChange={(event) => { setStepDelayMs(Number(event.target.value)); setResult(null); }}><option value={300}>Fast · 0.3s</option><option value={800}>Normal · 0.8s</option><option value={1500}>Slow · 1.5s</option><option value={2500}>Diagnostic · 2.5s</option></select></label>
        <label>Saved routine<select value={selectedProfileId} onChange={(event) => loadProfile(savedProfiles.find((entry) => entry.id === event.target.value))}><option value="">New routine</option>{savedProfiles.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}{entry.trainingState === 'validated' ? ' · enabled' : ' · draft'}</option>)}</select></label>
      </div>
      <p className="bluetooth-automation-trainer__hint">“Open Bluetooth settings” may open general Settings on this TV. Record whichever starting route is shortest and most repeatable.</p>
      <div className="bluetooth-automation-trainer__palette">
        {ACTIONS.map((action) => <button type="button" key={action} draggable={!liveAction} disabled={Boolean(liveAction)} onDragStart={(event) => setDragData(event, { source: 'palette', action })} onClick={() => recordAction(action)}>+ {liveAction === action ? 'sending…' : action.replaceAll('_', ' ')}</button>)}
        <button type="button" className="is-wait" draggable={!busy} disabled={Boolean(busy)} onDragStart={(event) => setDragData(event, { source: 'palette', kind: 'wait', durationMs: 2000 })} onClick={() => recordWait()}>+ Wait 2s</button>
        <button type="button" className="is-checkpoint" draggable={!busy} disabled={Boolean(busy)} onDragStart={(event) => setDragData(event, { source: 'palette', kind: 'checkpoint' })} onClick={() => addCheckpointAt(Number.isInteger(stepIndex) ? stepIndex + 1 : actions.length)}>+ Checkpoint</button>
      </div>
      <div className="bluetooth-automation-trainer__sequence" onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropStep(event, actions.length)}>
        {actions.length ? actions.map((action, index) => (
          <React.Fragment key={`${action}-${index}`}>
            <div className={`bluetooth-automation-trainer__step ${isWaitToken(action) ? 'is-wait' : ''}`}>
              <button type="button" className={stepIndex === index ? 'is-stepping' : ''} draggable onDragStart={(event) => { event.stopPropagation(); setDragData(event, { source: 'sequence', index }); }} onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => { event.stopPropagation(); dropStep(event, index); }} onClick={() => isWaitToken(action) ? editWait(index) : removeStep(index)}><small>{index + 1}</small>{formatSequenceStep(action)}<span>{isWaitToken(action) ? 'edit' : '×'}</span></button>
              {isWaitToken(action) ? <button type="button" className="bluetooth-automation-trainer__remove-wait" onClick={() => removeStep(index)} title="Remove wait">×</button> : null}
            </div>
            {stepCheckpoints[index] ? <button type="button" className={`is-checkpoint ${stepIndex === index ? 'is-stepping' : ''}`} onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => { event.stopPropagation(); dropStep(event, index + 1); }} onClick={() => { setStepCheckpoints((current) => current.map((entry, itemIndex) => itemIndex === index ? null : entry)); setResult(null); }}>✓ Checkpoint<span>×</span></button> : null}
          </React.Fragment>
        )) : <p>No actions recorded.</p>}
      </div>
      <div className="bluetooth-automation-trainer__controls"><button type="button" onClick={() => { setActions([]); setStepCheckpoints([]); setStepIndex(null); setProfileId(''); setSelectedProfileId(''); setResult(null); }}>New</button><button type="button" onClick={cloneRoutine} disabled={Boolean(busy) || !actions.length}>Clone</button><button type="button" className="is-step" onClick={executeNextStep} disabled={Boolean(busy) || !actions.length}>{busy === 'step' ? 'Stepping…' : 'Step →'}</button><button type="button" onClick={() => save()} disabled={Boolean(busy)}>Save</button><button type="button" className="is-execute" onClick={() => callRunner(false)} disabled={Boolean(busy)}>{busy === 'execute' ? 'Testing…' : `Test ${operation.replace('_', ' ')}`}</button>{telemetryConfirmed && <button type="button" className="is-enable" onClick={() => enableRoutine(false)} disabled={Boolean(busy)}>That worked — enable this routine</button>}{executionCompleted && !telemetryConfirmed && <button type="button" className="is-enable-manual" onClick={() => enableRoutine(true)} disabled={Boolean(busy)}>Enable with installer confirmation</button>}</div>
      {message && <p className={`bluetooth-automation-trainer__message ${result?.status === 'verified' ? 'is-success' : ''}`}>{message}</p>}
      {result && <details><summary>Checkpoint details</summary><pre>{JSON.stringify(result, null, 2)}</pre></details>}
      {checkpointEditor && <VisualCheckpointEditor screenshotDataUrl={checkpointEditor.screenshotDataUrl} stepIndex={checkpointEditor.stepIndex} routinePrefix={actions.slice(0, checkpointEditor.stepIndex + 1)} recoveryActions={ACTIONS} onSave={saveCheckpoint} onCancel={() => setCheckpointEditor(null)} />}
    </div>
  );
}
